// src/components/CalendarManagment.tsx

import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Typography,
    LinearProgress,
    MenuItem,
    Divider,
    Tooltip,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
    useGetSubCalendar,
    useCreateSubCalendar,
    useUpdateSubCalendar,
    useDeleteSubCalendar,
    useCreateRoleSubCalendar,
    useGetRoleSubCalendar,
    useUpdateRoleSubCalendar,
    useDeleteRoleSubCalendar,
} from "../../hooks/data/data_calendar";
import { ISubCalendar } from "../../interfaces/ISubCalendar";
import { IRoleSubCalendar } from "../../interfaces/ISubCalendar";
import { useGetUser } from "../../hooks/data/data_profile";

const CalendarManagment: React.FC = () => {
    // ─────────────── SubCalendar  ───────────────
    const { subCalendarArray, isLoadingSubCalendarArray, refetchSubCalendars } = useGetSubCalendar();
    const { updateSubCalendar, isUpdatingSubCalendar } = useUpdateSubCalendar();
    const { deleteSubCalendar, isDeletingSubCalendar } = useDeleteSubCalendar();
    const { createSubCalendar , isCreatingSubCalendar} = useCreateSubCalendar();

    const [openSubDialog, setOpenSubDialog] = useState<boolean>(false);
    const [currentSubCalendar, setCurrentSubCalendar] = useState<ISubCalendar | null>(null);
    const [subName, setSubName] = useState<string>("");
    const [subColor, setSubColor] = useState<string>("#000000");

    const handleOpenSubDialog = (calendar?: ISubCalendar) => {
        if (calendar) {
            setCurrentSubCalendar(calendar);
            setSubName(calendar.name);
            setSubColor(calendar.calendar_color);
        } else {
            setCurrentSubCalendar(null);
            setSubName("");
            setSubColor("#000000");
        }
        setOpenSubDialog(true);
    };

    const handleCloseSubDialog = () => {
        setOpenSubDialog(false);
    };

    const handleSaveSub = async () => {
        if (currentSubCalendar) {
            await updateSubCalendar({ id: currentSubCalendar.id, name: subName, calendar_color: subColor });
        } else {
            await createSubCalendar({ id: -1, name: subName, calendar_color: subColor });
        }
        setOpenSubDialog(false);
        await refetchSubCalendars();
        await refetchRoleSubCalendars();

    };

    const handleDeleteSub = async (id: number) => {
        await deleteSubCalendar(id);
        await refetchSubCalendars();
        await refetchRoleSubCalendars();
    };

    // ─────────────── RoleSubCalendar  ───────────────
    const { roleArray: rolesData, isLoadingRoleArray } = useGetUser();
    const { roleSubCalendarArray, isLoadingRoleSubCalendar, refetchRoleSubCalendars } = useGetRoleSubCalendar();
    const { createRoleSubCalendar, isCreatingRoleSubCalendar } = useCreateRoleSubCalendar();
    const { updateRoleSubCalendar, isUpdatingRoleSubCalendar } = useUpdateRoleSubCalendar();
    const { deleteRoleSubCalendar, isDeletingRoleSubCalendar } = useDeleteRoleSubCalendar();

    const [openRoleDialog, setOpenRoleDialog] = useState<boolean>(false);
    const [currentMapping, setCurrentMapping] = useState<IRoleSubCalendar | null>(null);
    const [selectedRole, setSelectedRole] = useState<number>(0);
    const [selectedSubCalForRole, setSelectedSubCalForRole] = useState<number>(0);

    const handleOpenRoleDialog = (mapping?: IRoleSubCalendar) => {
        if (mapping) {
            setCurrentMapping(mapping);
            setSelectedRole(mapping.role);
            setSelectedSubCalForRole(mapping.sub_calendar);
        } else {
            setCurrentMapping(null);
            setSelectedRole(0);
            setSelectedSubCalForRole(0);
        }
        setOpenRoleDialog(true);
    };

    const handleCloseRoleDialog = () => {
        setOpenRoleDialog(false);
    };

    const handleSaveRoleMapping = async () => {
        if (currentMapping) {
            await updateRoleSubCalendar({
                id: currentMapping.id,
                role: selectedRole,
                sub_calendar: selectedSubCalForRole,
            });
        } else {
            await createRoleSubCalendar({
                role: selectedRole,
                sub_calendar: selectedSubCalForRole,
            });
        }
        setOpenRoleDialog(false);
        await refetchRoleSubCalendars();

    };

    const handleDeleteRoleMapping = async (id: number) => {
        await refetchRoleSubCalendars();
        await deleteRoleSubCalendar(id);
    };

    // ─────────────── isLoading -> LinearProgress ───────────────
    if (
        isLoadingSubCalendarArray ||
        isUpdatingSubCalendar ||
        isDeletingSubCalendar ||
        isCreatingSubCalendar ||
        isLoadingRoleArray ||
        isLoadingRoleSubCalendar ||
        isUpdatingRoleSubCalendar ||
        isDeletingRoleSubCalendar ||
        isCreatingRoleSubCalendar
    ) {
        return <LinearProgress />;
    }

    // ─────────────── Gruppierung nach Rolle ───────────────
    // Mapping: roleId → Set<subCalendarId>
    const assignedByRole: Record<number, Set<number>> = {};
    rolesData?.forEach((role) => {
        assignedByRole[role.id] = new Set();
    });
    roleSubCalendarArray.forEach((mapping) => {
        if (!assignedByRole[mapping.role]) {
            assignedByRole[mapping.role] = new Set();
        }
        assignedByRole[mapping.role].add(mapping.sub_calendar);
    });

    return (
        <Box p={2}>
            {/* ─────────────── SubCalendar-Verwaltung ─────────────── */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center">
                    <Typography variant="h5">Kalender Verwaltung</Typography>
                    <Tooltip title="Hier können die Kalender von Kalender.Digital eingebunden werden. Hierfür bitte den selben Kalendernamen wie in Kalender.Digital eingeben">
                        <IconButton size="small" sx={{ ml: 1 }}>
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleOpenSubDialog()}>
                    Kalender hinzufügen
                </Button>
            </Box>

            <List>
                {subCalendarArray.map((calendar) => (
                    <ListItem
                        key={calendar.id}
                        secondaryAction={
                            <>
                                <IconButton edge="end" aria-label="edit" onClick={() => handleOpenSubDialog(calendar)}>
                                    <Edit />
                                </IconButton>
                                <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteSub(calendar.id)}>
                                    <Delete />
                                </IconButton>
                            </>
                        }
                    >
                        <ListItemText
                            primary={calendar.name}
                            secondary={
                                <>
                                    {`Farbe: ${calendar.calendar_color}`}
                                    <Box
                                        sx={{
                                            display: "inline-block",
                                            ml: 1,
                                            width: 20,
                                            height: 20,
                                            backgroundColor: calendar.calendar_color,
                                            border: "1px solid #ccc",
                                            verticalAlign: "middle",
                                        }}
                                    />
                                </>
                            }
                        />
                    </ListItem>
                ))}
            </List>

            <Dialog open={openSubDialog} onClose={handleCloseSubDialog}>
                <DialogTitle>{currentSubCalendar ? "Kalender bearbeiten" : "Neuen Kalender anlegen"}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Kalendername"
                        fullWidth
                        value={subName}
                        onChange={(e) => setSubName(e.target.value)}
                    />
                    <TextField
                        margin="dense"
                        label="Kalenderfarbe"
                        type="color"
                        fullWidth
                        value={subColor}
                        onChange={(e) => setSubColor(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSubDialog}>Abbrechen</Button>
                    <Button onClick={handleSaveSub}>Speichern</Button>
                </DialogActions>
            </Dialog>

            {/* ─────────────── Role-SubCalendar-Verwaltung ─────────────── */}
            <Divider sx={{ my: 2 }} />
            <Box mt={4} display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center">
                    <Typography variant="h5">Rollen‐Kalender Zuordnung</Typography>
                    <Tooltip title="Hier können rollenbasiert Kalender hinterlegt werden. Jeder Mitarbeiter in der ausgewählten Rolle kann die hinterlegten Kalender automatisch einsehen.">
                        <IconButton size="small" sx={{ ml: 1 }}>
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleOpenRoleDialog()}>
                    Neue Zuordnung
                </Button>
            </Box>

            {rolesData?.map((role) => {
                const entries = roleSubCalendarArray.filter((m) => m.role === role.id);
                if (entries.length === 0) return null;

                return (
                    <Box key={role.id} mb={3}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            {role.role}
                        </Typography>
                        <List disablePadding>
                            {entries.map((mapping) => {
                                const calendar = subCalendarArray.find((s) => s.id === mapping.sub_calendar);
                                return (
                                    <ListItem
                                        key={mapping.id}
                                        sx={{ pl: 4 }}
                                        secondaryAction={
                                            <>
                                                <IconButton edge="end" aria-label="edit" onClick={() => handleOpenRoleDialog(mapping)}>
                                                    <Edit />
                                                </IconButton>
                                                <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteRoleMapping(mapping.id)}>
                                                    <Delete />
                                                </IconButton>
                                            </>
                                        }
                                    >
                                        <ListItemText primary={calendar?.name || ""} />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                );
            })}

            <Dialog open={openRoleDialog} onClose={handleCloseRoleDialog}>
                <DialogTitle>{currentMapping ? "Zuordnung bearbeiten" : "Neue Zuordnung anlegen"}</DialogTitle>
                <DialogContent>
                    <Box mb={2} mt={2}>
                        <TextField
                            select
                            label="Rolle"
                            fullWidth
                            value={selectedRole}
                            onChange={(e) => {
                                const newRole = Number(e.target.value);
                                setSelectedRole(newRole);
                                setSelectedSubCalForRole(0);
                            }}
                        >
                            <MenuItem value={0} disabled>
                                Rolle wählen
                            </MenuItem>
                            {rolesData?.map((r) => (
                                <MenuItem key={r.id} value={r.id}>
                                    {r.role}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box mb={2}>
                        <TextField
                            select
                            label="Kalender"
                            fullWidth
                            value={selectedSubCalForRole}
                            onChange={(e) => setSelectedSubCalForRole(Number(e.target.value))}
                        >
                            <MenuItem value={0} disabled>
                                Kalender wählen
                            </MenuItem>
                            {subCalendarArray.map((s) => {
                                const conflict = roleSubCalendarArray.some(
                                    (m) =>
                                        m.role === selectedRole &&
                                        m.sub_calendar === s.id &&
                                        (currentMapping ? m.id !== currentMapping.id : true)
                                );

                                return (
                                    <MenuItem key={s.id} value={s.id} disabled={conflict}>
                                        {s.name}
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseRoleDialog}>Abbrechen</Button>
                    <Button
                        onClick={handleSaveRoleMapping}
                        disabled={selectedRole === 0 || selectedSubCalForRole === 0}
                    >
                        {currentMapping ? "Speichern" : "Anlegen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CalendarManagment;
